import { useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useToast } from '@chakra-ui/react'
import { Capacitor } from '@capacitor/core'
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
} from '@capacitor/push-notifications'
import * as Sentry from '@sentry/react'
import { useAuth } from '../hooks/useAuth'
import { NOTIFICATION_KEYS } from '../hooks/useNotifications'
import { useBulkActionsStore } from '../stores/bulkActionsStore'
import handleSuccessToast from './common/toast/handleSuccessToast'
import handleErrorToast from './common/toast/handleErrorToast'
import type { FCMPushData } from '../types/notification.types'
import type { BulkActionStatus } from '../types/store/bulkActions.types'

type ToastFn = ReturnType<typeof useToast>

const navigateTo = (path: string) => {
  if (!path) return
  const normalized = path.startsWith('/') ? path : `/${path}`
  globalThis.location.hash = normalized
}

const mapProgressStatus = (status?: string): BulkActionStatus => {
  switch (status) {
    case 'completed':
      return 'completed'
    case 'failed':
    case 'error':
      return 'error'
    case 'cancelled':
      return 'cancelled'
    case 'in_progress':
    default:
      return 'running'
  }
}

const applyProgressFromPush = (data: FCMPushData) => {
  const { job_id, processed, total, status } = data
  if (!job_id) return
  useBulkActionsStore.getState().updateProgressFromPush({
    jobId: job_id,
    processed: processed !== undefined ? Number(processed) : undefined,
    total: total !== undefined ? Number(total) : undefined,
    status: mapProgressStatus(status),
  })
}

const markBulkComplete = (jobId: string | undefined, status: BulkActionStatus) => {
  if (!jobId) return
  useBulkActionsStore.getState().markComplete(jobId, status)
}

export const FCMListener = () => {
  const { isAuthenticated } = useAuth()
  const qc = useQueryClient()
  const toast = useToast()

  const qcRef = useRef<QueryClient>(qc)
  const toastRef = useRef<ToastFn>(toast)
  qcRef.current = qc
  toastRef.current = toast

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isAuthenticated) return

    let cancelled = false
    let receivedHandle: { remove: () => void } | null = null
    let actionHandle: { remove: () => void } | null = null

    const onReceived = (notification: PushNotificationSchema) => {
      const data = (notification.data ?? {}) as FCMPushData
      try {
        const title = notification.title ?? ''
        const body = notification.body ?? ''
        const invalidateNotifications = () =>
          qcRef.current.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all() })

        const backendJobId = data.task_id ?? data.job_id

        const isNative = Capacitor.isNativePlatform()
        switch (data.type) {
          case 'progress':
            applyProgressFromPush(data)
            return
          case 'EXPORT_COMPLETED':
          case 'IMPORT_COMPLETED':
            markBulkComplete(backendJobId, 'completed')
            if (!isNative) handleSuccessToast(toastRef.current, title, body)
            invalidateNotifications()
            return
          case 'EXPORT_FAILED':
          case 'IMPORT_FAILED':
            markBulkComplete(backendJobId, 'error')
            if (!isNative) handleErrorToast(toastRef.current, title, body)
            invalidateNotifications()
            return
          default:
            if (!title && !body) return
            if (!isNative) handleSuccessToast(toastRef.current, title, body)
            invalidateNotifications()
        }
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            feature: 'fcm-listener',
            handler: 'received',
            push_type: data.type ?? 'unknown',
          },
        })
      }
    }

    const onAction = (action: ActionPerformed) => {
      const data = (action.notification.data ?? {}) as FCMPushData
      try {
        if (data.navigate) navigateTo(data.navigate)
      } catch (err) {
        Sentry.captureException(err, {
          tags: {
            feature: 'fcm-listener',
            handler: 'action',
            push_type: data.type ?? 'unknown',
          },
        })
      }
    }

    const captureRegisterError = (handler: 'received' | 'action') => (err: unknown) => {
      Sentry.captureException(err, {
        tags: { feature: 'fcm-listener', handler, stage: 'register' },
      })
    }

    PushNotifications.addListener('pushNotificationReceived', onReceived)
      .then((h) => {
        if (cancelled) h.remove()
        else receivedHandle = h
      })
      .catch(captureRegisterError('received'))
    PushNotifications.addListener('pushNotificationActionPerformed', onAction)
      .then((h) => {
        if (cancelled) h.remove()
        else actionHandle = h
      })
      .catch(captureRegisterError('action'))

    return () => {
      cancelled = true
      receivedHandle?.remove()
      actionHandle?.remove()
    }
  }, [isAuthenticated])

  return null
}

export default FCMListener
